import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import useAdminAuth from '../hooks/useAdminAuth';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Compass, MapPin, Phone, Mail, CheckCircle, Clock, XCircle, Shield, Camera, Building, CreditCard, Edit3, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPilotRegistrations = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getAdminEmail } = useAdminAuth();
  const [regs, setRegs] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [availabilitiesByReg, setAvailabilitiesByReg] = useState({}); // { reg_id: [listings] }
  const [editListing, setEditListing] = useState(null); // { reg_id, listing, form: {...} }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkForm, setBulkForm] = useState(null); // { base_suburb, base_state, create_if_missing }
  const [editProfile, setEditProfile] = useState(null); // { reg, form }
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileErr, setProfileErr] = useState('');

  useEffect(() => { fetchRegs(); }, []);
  useEffect(() => {
    setFiltered(filter === 'all' ? regs : regs.filter(r => r.status === filter));
  }, [regs, filter]);

  const fetchRegs = async () => {
    try {
      const res = await axios.get(`${API}/pilot-registrations`);
      setRegs(res.data);
    } catch { toast({ title: 'Error', description: 'Failed to load', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/pilot-registrations/${id}/status`, {
        status, admin_email: getAdminEmail()
      });
      toast({ title: 'Success', description: `Status updated to ${status}` });
      fetchRegs();
    } catch { toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' }); }
  };

  const toggleSteererVerified = async (reg, verified) => {
    try {
      await axios.post(
        `${API}/admin/pilots/${encodeURIComponent(reg.email)}/verify-steerer?admin_email=${encodeURIComponent(getAdminEmail())}`,
        { verified, pilot_registration_id: reg.id }
      );
      toast({ title: verified ? 'Verified' : 'Unverified', description: `Steerer status updated for ${reg.name}` });
      fetchRegs();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const fetchListingsFor = async (regId) => {
    try {
      const res = await axios.get(`${API}/admin/pilots/registrations/${regId}/availabilities?admin_email=${encodeURIComponent(getAdminEmail())}`);
      setAvailabilitiesByReg(prev => ({ ...prev, [regId]: res.data || [] }));
    } catch (e) { /* ignore */ }
  };

  const openEditProfile = async (reg) => {
    setProfileErr('');
    setEditProfile({
      reg,
      form: {
        pilot_code: reg.pilot_code || '',
        name: reg.name || '',
        phone: reg.phone || '',
        email: reg.email || '',
        company_name: reg.company_name || '',
        abn: reg.abn || '',
        address: reg.address || '',
        bank_account_name: reg.bank_account_name || '',
        bank_bsb: reg.bank_bsb || '',
        bank_account_number: reg.bank_account_number || '',
        is_agent: !!reg.is_agent,
        is_steerer: !!reg.is_steerer,
        notes: reg.notes || '',
      },
      docs: [],
      docsLoading: true,
    });
    try {
      const res = await axios.get(
        `${API}/admin/pilots/registrations/${reg.id}/documents?admin_email=${encodeURIComponent(getAdminEmail())}`
      );
      setEditProfile((prev) => prev ? { ...prev, docs: res.data || [], docsLoading: false } : prev);
    } catch (e) {
      setEditProfile((prev) => prev ? { ...prev, docs: [], docsLoading: false } : prev);
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onerror = reject;
      r.onload = () => resolve(r.result);
      r.readAsDataURL(file);
    });

  const handleUploadDoc = async (file, docType, expiry) => {
    if (!editProfile || !file) return;
    const type = (docType || '').trim() || 'Accreditation';
    try {
      const dataUrl = await fileToBase64(file);
      const res = await axios.post(
        `${API}/admin/pilots/registrations/${editProfile.reg.id}/documents?admin_email=${encodeURIComponent(getAdminEmail())}`,
        {
          doc_type: type,
          expiry_date: expiry || null,
          file_name: file.name,
          file_base64: dataUrl,
        },
      );
      setEditProfile((prev) => prev ? { ...prev, docs: [res.data, ...prev.docs] } : prev);
      toast({ title: 'Uploaded', description: `${file.name} attached to ${editProfile.reg.name}` });
    } catch (e) {
      toast({ title: 'Upload failed', description: e.response?.data?.detail || 'Please try again', variant: 'destructive' });
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!editProfile) return;
    try {
      await axios.delete(
        `${API}/admin/pilots/documents/${docId}?admin_email=${encodeURIComponent(getAdminEmail())}`
      );
      setEditProfile((prev) => prev ? { ...prev, docs: prev.docs.filter((d) => d.id !== docId) } : prev);
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Please try again', variant: 'destructive' });
    }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const res = await axios.get(
        `${API}/admin/pilots/documents/${doc.id}/download?admin_email=${encodeURIComponent(getAdminEmail())}`
      );
      const b64 = res.data.file_base64 || '';
      const raw = b64.includes(',') ? b64.split(',')[1] : b64;
      // Trigger browser download from a data URL
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${raw}`;
      link.download = res.data.file_name || 'accreditation';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast({ title: 'Download failed', description: e.response?.data?.detail || 'Please try again', variant: 'destructive' });
    }
  };

  const handleSaveProfile = async () => {
    if (!editProfile) return;
    const { reg, form } = editProfile;
    setProfileErr('');
    if (!form.name.trim() || !form.email.trim()) {
      setProfileErr('Name and email are required'); return;
    }
    setSavingProfile(true);
    try {
      await axios.patch(
        `${API}/admin/pilots/registrations/${reg.id}?admin_email=${encodeURIComponent(getAdminEmail())}`,
        form,
      );
      toast({ title: 'Saved', description: 'Pilot profile updated' });
      setEditProfile(null);
      fetchRegs();
    } catch (e) {
      setProfileErr(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleToggleExpand = (regId) => {
    const nowOpen = expandedId !== regId;
    setExpandedId(nowOpen ? regId : null);
    if (nowOpen && availabilitiesByReg[regId] === undefined) {
      fetchListingsFor(regId);
    }
  };

  const openEditListing = (regId, listing) => {
    setEditListing({
      regId,
      listing,
      form: {
        base_suburb: listing?.base_suburb || '',
        base_state: listing?.base_state || 'NSW',
        available_from: listing?.available_from || '',
        available_until: listing?.available_until || '',
        is_available_now: listing?.is_available_now !== false,
        always_visible: !!listing?.always_visible,
        notes: listing?.notes || '',
      },
    });
  };

  const openCreateListing = (regId) => openEditListing(regId, null);

  const toggleSelected = (regId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(regId)) next.delete(regId); else next.add(regId);
      return next;
    });
  };

  const openBulkModal = () => setBulkForm({ base_suburb: '', base_state: 'NSW', create_if_missing: false });

  const submitBulkLocation = async () => {
    if (!bulkForm.base_suburb.trim() || !bulkForm.base_state.trim()) {
      toast({ title: 'Error', description: 'Suburb and state are required', variant: 'destructive' });
      return;
    }
    try {
      const res = await axios.post(
        `${API}/admin/pilots/bulk-location?admin_email=${encodeURIComponent(getAdminEmail())}`,
        {
          pilot_registration_ids: Array.from(selectedIds),
          base_suburb: bulkForm.base_suburb,
          base_state: bulkForm.base_state,
          create_if_missing: bulkForm.create_if_missing,
        }
      );
      toast({ title: 'Bulk update complete', description: res.data.message });
      // Refresh any expanded listings
      const affected = Array.from(selectedIds).filter(id => availabilitiesByReg[id] !== undefined);
      await Promise.all(affected.map(id => fetchListingsFor(id)));
      setBulkForm(null);
      setSelectedIds(new Set());
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Bulk update failed', variant: 'destructive' });
    }
  };

  const saveEditListing = async () => {
    if (!editListing) return;
    const { regId, listing, form } = editListing;
    if (!form.base_suburb.trim() || !form.base_state.trim()) {
      toast({ title: 'Error', description: 'Suburb and state are required', variant: 'destructive' });
      return;
    }
    try {
      if (listing) {
        await axios.patch(`${API}/admin/pilots/availability/${listing.id}?admin_email=${encodeURIComponent(getAdminEmail())}`, form);
        toast({ title: 'Updated', description: 'Location & availability saved' });
      } else {
        await axios.post(`${API}/admin/pilots/registrations/${regId}/availabilities?admin_email=${encodeURIComponent(getAdminEmail())}`, form);
        toast({ title: 'Created', description: 'New listing created for this pilot' });
      }
      setEditListing(null);
      fetchListingsFor(regId);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    }
  };

  const assignPilotCode = async (reg) => {
    const input = window.prompt(
      `Enter a pilot code for ${reg.name}\n(3-20 characters, e.g. P-JOHN or leave blank to auto-generate)`,
      reg.pilot_code || ''
    );
    if (input === null) return; // cancelled
    try {
      const res = await axios.post(
        `${API}/admin/pilots/${reg.id}/assign-code?admin_email=${encodeURIComponent(getAdminEmail())}`,
        { pilot_code: (input || '').trim() }
      );
      toast({ title: 'Code assigned', description: `${reg.name} → ${res.data.pilot_code}` });
      fetchRegs();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const stats = {
    total: regs.length,
    pending: regs.filter(r => r.status === 'pending').length,
    approved: regs.filter(r => r.status === 'approved').length,
    rejected: regs.filter(r => r.status === 'rejected').length
  };

  if (loading) return <div className="min-h-screen bg-gray-50"><Header /><div className="container mx-auto px-4 py-12 text-center"><p>Loading...</p></div><Footer /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#1e3a5f]">Pilot/Steerer Registrations</h1>
              <p className="text-gray-600 mt-2">Review and approve pilot registrations</p>
            </div>
            <Button onClick={() => navigate('/admin')} variant="outline">Back to Dashboard</Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-5"><p className="text-sm text-gray-600">Total</p><p className="text-3xl font-bold text-[#1e3a5f]">{stats.total}</p></Card>
            <Card className="p-5"><p className="text-sm text-gray-600">Pending</p><p className="text-3xl font-bold text-yellow-600">{stats.pending}</p></Card>
            <Card className="p-5"><p className="text-sm text-gray-600">Approved</p><p className="text-3xl font-bold text-green-600">{stats.approved}</p></Card>
            <Card className="p-5"><p className="text-sm text-gray-600">Rejected</p><p className="text-3xl font-bold text-red-600">{stats.rejected}</p></Card>
          </div>

          <Card className="p-4 mb-6">
            <Label>Filter</Label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {['all', 'pending', 'approved', 'rejected', 'suspended'].map(s => (
                <Button key={s} variant={filter === s ? 'default' : 'outline'} onClick={() => setFilter(s)} size="sm" className="capitalize">{s === 'all' ? 'All' : s}</Button>
              ))}
            </div>
          </Card>

          {selectedIds.size > 0 && (
            <div className="sticky top-16 z-40 mb-4 flex items-center justify-between gap-3 bg-blue-600 text-white rounded-lg px-4 py-3 shadow-lg" data-testid="bulk-action-bar">
              <div className="text-sm">
                <strong>{selectedIds.size}</strong> pilot{selectedIds.size === 1 ? '' : 's'} selected
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())} className="bg-white text-blue-700 border-white hover:bg-blue-50" data-testid="bulk-clear">
                  Clear
                </Button>
                <Button size="sm" onClick={openBulkModal} className="bg-white text-blue-700 hover:bg-blue-50" data-testid="bulk-relocate-btn">
                  <MapPin className="w-3.5 h-3.5 mr-1" /> Bulk update location
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {filtered.length === 0 ? (
              <Card className="p-12 text-center"><Compass className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-600">No registrations found</p></Card>
            ) : filtered.map(reg => (
              <Card key={reg.id} className={`p-6 ${selectedIds.has(reg.id) ? 'ring-2 ring-blue-400' : ''}`} data-testid={`pilot-reg-${reg.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <label className="flex items-start pt-1 cursor-pointer" title="Select for bulk actions">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(reg.id)}
                      onChange={() => toggleSelected(reg.id)}
                      className="w-4 h-4"
                      data-testid={`pilot-reg-check-${reg.id}`}
                    />
                  </label>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        reg.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        reg.status === 'approved' ? 'bg-green-100 text-green-800' :
                        reg.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>{reg.status}</span>
                      <span className="text-xs text-gray-500">{new Date(reg.created_at).toLocaleString()}</span>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-1">{reg.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{reg.company_name}</p>

                    {/* Pilot code row + assign button */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs text-gray-500">Pilot Code:</span>
                      {reg.pilot_code ? (
                        <span className="font-mono text-sm font-semibold text-[#1e3a5f]" data-testid={`pilot-code-${reg.id}`}>{reg.pilot_code}</span>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Not assigned</span>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => assignPilotCode(reg)}
                        className="text-xs h-6 px-2"
                        data-testid={`assign-code-${reg.id}`}
                      >
                        {reg.pilot_code ? 'Change' : 'Assign'} Code
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3 text-sm mb-3">
                      <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" />{reg.email}</div>
                      <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" />{reg.phone}</div>
                      <div className="flex items-center gap-2"><Building className="w-4 h-4 text-gray-400" />ABN: {reg.abn}</div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3"><MapPin className="w-4 h-4 inline text-gray-400 mr-1" />{reg.address}</p>

                    {/* Expand/Collapse */}
                    <button onClick={() => handleToggleExpand(reg.id)} className="text-sm text-[#1e3a5f] font-semibold hover:underline mb-3">
                      {expandedId === reg.id ? 'Hide Details' : 'Show Full Details'}
                    </button>

                    {expandedId === reg.id && (
                      <div className="space-y-4 mt-3 border-t pt-4">
                        {/* Bank Details */}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> Bank Details</p>
                          <div className="grid grid-cols-3 gap-2 text-sm">
                            <div><span className="text-gray-500">Name:</span> <strong>{reg.bank_account_name}</strong></div>
                            <div><span className="text-gray-500">BSB:</span> <strong>{reg.bank_bsb}</strong></div>
                            <div><span className="text-gray-500">Account:</span> <strong>{reg.bank_account_number}</strong></div>
                          </div>
                        </div>

                        {/* Documents */}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Insurance & Documents</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                            {[
                              ['Public Liability', reg.public_liability, true],
                              ['Vehicle Insurance', reg.vehicle_insurance, true],
                              ['Pilot Insurance', reg.pilot_insurance, true],
                              ['Workers Comp', reg.workers_comp, true],
                              ['Steerer Insurance', reg.steerer_insurance, false],
                              ['Accreditations', reg.pilot_accreditations, false],
                              ['MSIC', reg.msic, false],
                              ['Port Passes', reg.port_passes, false],
                            ].map(([label, doc, mandatory]) => (
                              <div key={label} className="flex items-center gap-1.5">
                                {doc ? <CheckCircle className="w-4 h-4 text-green-600" /> : mandatory ? <XCircle className="w-4 h-4 text-red-500" /> : <Clock className="w-4 h-4 text-gray-300" />}
                                <span className={doc ? 'text-green-700' : mandatory ? 'text-red-600' : 'text-gray-400'}>{label}</span>
                                {doc && <a href={doc} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline ml-1">View</a>}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Vehicle Photos */}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Vehicle Photos</p>
                          <div className="grid grid-cols-2 gap-3">
                            {reg.vehicle_photo_front && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Front 3/4</p>
                                <img src={reg.vehicle_photo_front} alt="Front" className="w-full h-40 object-cover rounded-lg border cursor-pointer" onClick={() => window.open(reg.vehicle_photo_front, '_blank')} />
                              </div>
                            )}
                            {reg.vehicle_photo_rear && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Rear 3/4</p>
                                <img src={reg.vehicle_photo_rear} alt="Rear" className="w-full h-40 object-cover rounded-lg border cursor-pointer" onClick={() => window.open(reg.vehicle_photo_rear, '_blank')} />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Location & Availability (admin can update on the pilot's behalf) */}
                        <div className="bg-blue-50 rounded-lg p-3" data-testid={`admin-reg-availability-${reg.id}`}>
                          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <p className="text-xs font-semibold text-blue-800 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Location & Availability</p>
                            <Button size="sm" variant="outline" onClick={() => openCreateListing(reg.id)} className="text-xs text-blue-700 border-blue-400" data-testid={`admin-reg-add-listing-${reg.id}`}>
                              <Plus className="w-3.5 h-3.5 mr-1" /> New listing
                            </Button>
                          </div>
                          {(availabilitiesByReg[reg.id] === undefined) ? (
                            <p className="text-xs text-gray-500 italic">Loading listings…</p>
                          ) : availabilitiesByReg[reg.id].length === 0 ? (
                            <p className="text-xs text-gray-500 italic">No availability listing yet. Click &quot;New listing&quot; to create one for this pilot.</p>
                          ) : (
                            <div className="space-y-2">
                              {availabilitiesByReg[reg.id].map(l => (
                                <div key={l.id} className="flex items-start justify-between gap-2 bg-white rounded border p-2 text-xs" data-testid={`admin-reg-listing-${l.id}`}>
                                  <div>
                                    <div className="font-semibold text-[#1e3a5f]">{l.base_suburb || '—'}, {l.base_state || '—'}
                                      {typeof l.base_lat === 'number' && <span className="ml-2 font-mono text-gray-400 text-[10px]">({l.base_lat.toFixed(3)}, {l.base_lng?.toFixed(3)})</span>}
                                    </div>
                                    <div className="mt-0.5 text-gray-600 flex gap-2 flex-wrap">
                                      <span>{l.available_from || 'no start'} → {l.available_until || 'ongoing'}</span>
                                      <span className={`px-1.5 rounded ${l.is_available_now !== false ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                                        {l.is_available_now !== false ? 'Available now' : 'Not available'}
                                      </span>
                                      {l.always_visible && <span className="px-1.5 rounded bg-purple-100 text-purple-700">Always visible</span>}
                                    </div>
                                  </div>
                                  <Button size="sm" variant="outline" onClick={() => openEditListing(reg.id, l)} className="text-xs text-blue-700 border-blue-300" data-testid={`admin-reg-edit-listing-${l.id}`}>
                                    <Edit3 className="w-3 h-3 mr-1" /> Edit
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditProfile(reg)}
                      className="text-blue-700 border-blue-300"
                      data-testid={`edit-profile-${reg.id}`}
                    >
                      <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit profile
                    </Button>
                    {reg.status === 'pending' && (
                      <>
                        <Button size="sm" onClick={() => updateStatus(reg.id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white">Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => updateStatus(reg.id, 'rejected')} className="text-red-600 border-red-300">Reject</Button>
                      </>
                    )}
                    {reg.status === 'approved' && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(reg.id, 'suspended')} className="text-orange-600 border-orange-300">Suspend</Button>
                    )}
                    {(reg.status === 'rejected' || reg.status === 'suspended') && (
                      <Button size="sm" onClick={() => updateStatus(reg.id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white">Approve</Button>
                    )}

                    {/* Steerer verification — only shown for approved pilots offering steerer service */}
                    {reg.status === 'approved' && (
                      reg.steerer_insurance || reg.services_offered?.includes?.('steerer') || true
                    ) && (
                      <div className="mt-2 pt-2 border-t border-dashed w-full">
                        {reg.steerer_verified ? (
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-green-700 font-semibold">✓ Verified Steerer</span>
                            <Button size="sm" variant="outline" onClick={() => toggleSteererVerified(reg, false)} className="text-xs text-orange-600 border-orange-300" data-testid={`unverify-steerer-${reg.id}`}>Unverify</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => toggleSteererVerified(reg, true)} className="text-xs text-blue-600 border-blue-300" data-testid={`verify-steerer-${reg.id}`}>Verify Steerer</Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Bulk location modal */}
      {bulkForm && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setBulkForm(null)}
          data-testid="bulk-modal-backdrop"
        >
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#1e3a5f] mb-1 flex items-center gap-2">
              <MapPin className="w-5 h-5" /> Bulk update location
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Setting the same base location on <strong>{selectedIds.size} pilot{selectedIds.size === 1 ? '' : 's'}</strong>. Lat/lng auto-geocoded.
            </p>
            <div className="space-y-3">
              <div className="grid grid-cols-[2fr_1fr] gap-3">
                <div>
                  <Label>Suburb *</Label>
                  <Input
                    value={bulkForm.base_suburb}
                    onChange={(e) => setBulkForm({ ...bulkForm, base_suburb: e.target.value })}
                    placeholder="e.g. Brisbane"
                    data-testid="bulk-suburb"
                  />
                </div>
                <div>
                  <Label>State *</Label>
                  <select
                    value={bulkForm.base_state}
                    onChange={(e) => setBulkForm({ ...bulkForm, base_state: e.target.value })}
                    className="w-full h-10 border rounded-md px-3 text-sm"
                    data-testid="bulk-state"
                  >
                    {['NSW','VIC','QLD','SA','WA','TAS','NT','ACT'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkForm.create_if_missing}
                  onChange={(e) => setBulkForm({ ...bulkForm, create_if_missing: e.target.checked })}
                  data-testid="bulk-create-if-missing"
                />
                <span>Also <strong>create a new listing</strong> for pilots that don&apos;t have one yet</span>
              </label>
              <div className="flex gap-2 mt-2">
                <Button variant="outline" onClick={() => setBulkForm(null)} className="flex-1" data-testid="bulk-cancel">Cancel</Button>
                <Button onClick={submitBulkLocation} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="bulk-submit">
                  Update {selectedIds.size} pilot{selectedIds.size === 1 ? '' : 's'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit / Create listing modal */}
      {editListing && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setEditListing(null)}
          data-testid="admin-reg-edit-backdrop"
        >
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#1e3a5f] mb-1 flex items-center gap-2">
              <MapPin className="w-5 h-5" /> {editListing.listing ? 'Update' : 'Create'} location & availability
            </h3>
            <p className="text-xs text-gray-500 mb-4">Latitude/longitude will be auto-geocoded from suburb + state.</p>
            <div className="space-y-3">
              <div className="grid grid-cols-[2fr_1fr] gap-3">
                <div>
                  <Label>Suburb *</Label>
                  <Input
                    value={editListing.form.base_suburb}
                    onChange={(e) => setEditListing({ ...editListing, form: { ...editListing.form, base_suburb: e.target.value } })}
                    placeholder="e.g. Newcastle"
                    data-testid="admin-reg-modal-suburb"
                  />
                </div>
                <div>
                  <Label>State *</Label>
                  <select
                    value={editListing.form.base_state}
                    onChange={(e) => setEditListing({ ...editListing, form: { ...editListing.form, base_state: e.target.value } })}
                    className="w-full h-10 border rounded-md px-3 text-sm"
                    data-testid="admin-reg-modal-state"
                  >
                    {['NSW','VIC','QLD','SA','WA','TAS','NT','ACT'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Available from</Label>
                  <Input
                    type="date"
                    value={editListing.form.available_from || ''}
                    onChange={(e) => setEditListing({ ...editListing, form: { ...editListing.form, available_from: e.target.value } })}
                    data-testid="admin-reg-modal-from"
                  />
                </div>
                <div>
                  <Label>Available until <span className="text-xs text-gray-400">(optional)</span></Label>
                  <Input
                    type="date"
                    value={editListing.form.available_until || ''}
                    onChange={(e) => setEditListing({ ...editListing, form: { ...editListing.form, available_until: e.target.value } })}
                    data-testid="admin-reg-modal-until"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={editListing.form.is_available_now}
                  onChange={(e) => setEditListing({ ...editListing, form: { ...editListing.form, is_available_now: e.target.checked } })}
                  data-testid="admin-reg-modal-available-now"
                />
                <span><strong>Available now</strong> — shows on the map as ready to take work</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={editListing.form.always_visible}
                  onChange={(e) => setEditListing({ ...editListing, form: { ...editListing.form, always_visible: e.target.checked } })}
                  data-testid="admin-reg-modal-always-visible"
                />
                <span><strong>Always visible</strong> — stay on the map even after the end date</span>
              </label>
              <div className="flex gap-2 mt-2">
                <Button variant="outline" onClick={() => setEditListing(null)} className="flex-1" data-testid="admin-reg-modal-cancel">Cancel</Button>
                <Button onClick={saveEditListing} className="flex-1 bg-[#1e3a5f] hover:bg-[#15304f] text-white" data-testid="admin-reg-modal-save">
                  {editListing.listing ? 'Save changes' : 'Create listing'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editProfile && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
          onClick={() => !savingProfile && setEditProfile(null)}
          data-testid="edit-profile-backdrop"
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-2xl w-full shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-[#1e3a5f] mb-1 flex items-center gap-2">
              <Edit3 className="w-5 h-5" /> Edit pilot profile
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              {editProfile.reg.pilot_code ? `#${editProfile.reg.pilot_code} · ` : ''}
              {editProfile.reg.email}
            </p>
            <div className="space-y-4">
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-md">
                <Label className="text-xs uppercase tracking-wider text-purple-800 font-semibold">Pilot ID *</Label>
                <Input
                  value={editProfile.form.pilot_code}
                  onChange={(e) => setEditProfile({ ...editProfile, form: { ...editProfile.form, pilot_code: e.target.value.toUpperCase().slice(0, 20) } })}
                  className="font-mono uppercase mt-1"
                  placeholder="P00011"
                  data-testid="edit-profile-code"
                />
                <p className="text-[11px] text-purple-800/80 mt-1">
                  Format is normally P##### (e.g. P00011). Must be unique across all pilots.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Full name *</Label>
                  <Input
                    value={editProfile.form.name}
                    onChange={(e) => setEditProfile({ ...editProfile, form: { ...editProfile.form, name: e.target.value } })}
                    data-testid="edit-profile-name"
                  />
                </div>
                <div>
                  <Label>Company name</Label>
                  <Input
                    value={editProfile.form.company_name}
                    onChange={(e) => setEditProfile({ ...editProfile, form: { ...editProfile.form, company_name: e.target.value } })}
                    data-testid="edit-profile-company"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editProfile.form.phone}
                    onChange={(e) => setEditProfile({ ...editProfile, form: { ...editProfile.form, phone: e.target.value } })}
                    data-testid="edit-profile-phone"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={editProfile.form.email}
                    onChange={(e) => setEditProfile({ ...editProfile, form: { ...editProfile.form, email: e.target.value } })}
                    data-testid="edit-profile-email"
                  />
                </div>
                <div>
                  <Label>ABN</Label>
                  <Input
                    value={editProfile.form.abn}
                    onChange={(e) => setEditProfile({ ...editProfile, form: { ...editProfile.form, abn: e.target.value } })}
                    data-testid="edit-profile-abn"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Address</Label>
                  <Input
                    value={editProfile.form.address}
                    onChange={(e) => setEditProfile({ ...editProfile, form: { ...editProfile.form, address: e.target.value } })}
                    data-testid="edit-profile-address"
                  />
                </div>
              </div>

              <div className="pt-3 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Bank details</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <Label>Account name</Label>
                    <Input
                      value={editProfile.form.bank_account_name}
                      onChange={(e) => setEditProfile({ ...editProfile, form: { ...editProfile.form, bank_account_name: e.target.value } })}
                      data-testid="edit-profile-bank-name"
                    />
                  </div>
                  <div>
                    <Label>BSB</Label>
                    <Input
                      value={editProfile.form.bank_bsb}
                      onChange={(e) => setEditProfile({ ...editProfile, form: { ...editProfile.form, bank_bsb: e.target.value } })}
                      data-testid="edit-profile-bsb"
                    />
                  </div>
                  <div>
                    <Label>Account number</Label>
                    <Input
                      value={editProfile.form.bank_account_number}
                      onChange={(e) => setEditProfile({ ...editProfile, form: { ...editProfile.form, bank_account_number: e.target.value } })}
                      data-testid="edit-profile-bank-account"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t border-gray-100 space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editProfile.form.is_agent}
                    onChange={(e) => setEditProfile({ ...editProfile, form: { ...editProfile.form, is_agent: e.target.checked } })}
                    className="w-4 h-4 accent-[#1e3a5f]"
                    data-testid="edit-profile-is-agent"
                  />
                  Agent — can register sub-pilots
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editProfile.form.is_steerer}
                    onChange={(e) => setEditProfile({ ...editProfile, form: { ...editProfile.form, is_steerer: e.target.checked } })}
                    className="w-4 h-4 accent-[#1e3a5f]"
                    data-testid="edit-profile-is-steerer"
                  />
                  Offers steerer service
                </label>
              </div>

              <div>
                <Label>Internal notes (admin only)</Label>
                <Input
                  value={editProfile.form.notes}
                  onChange={(e) => setEditProfile({ ...editProfile, form: { ...editProfile.form, notes: e.target.value } })}
                  placeholder="Free-text notes visible to admin only"
                  data-testid="edit-profile-notes"
                />
              </div>

              <div className="pt-3 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Accreditations &amp; documents</p>
                <div className="space-y-2" data-testid="edit-profile-docs">
                  {editProfile.docsLoading && (
                    <p className="text-xs text-gray-500">Loading documents…</p>
                  )}
                  {!editProfile.docsLoading && editProfile.docs.length === 0 && (
                    <p className="text-xs text-gray-500">No accreditations uploaded yet.</p>
                  )}
                  {editProfile.docs.map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-2 border rounded p-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-[#1e3a5f] truncate">{d.doc_type}</p>
                        <p className="text-[11px] text-gray-500 truncate">
                          {d.file_name}
                          {d.expiry_date ? ` · expires ${d.expiry_date}` : ''}
                        </p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button size="sm" variant="outline" onClick={() => handleDownloadDoc(d)} data-testid={`doc-dl-${d.id}`}>
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteDoc(d.id)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          data-testid={`doc-del-${d.id}`}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="border rounded-lg p-3 bg-slate-50 space-y-2">
                    <div className="grid sm:grid-cols-[1fr_1fr] gap-2">
                      <div>
                        <Label className="text-xs">Document type</Label>
                        <Input
                          value={editProfile.newDocType || ''}
                          onChange={(e) => setEditProfile({ ...editProfile, newDocType: e.target.value })}
                          placeholder="e.g. Pilot certificate, MSIC, Cert IV"
                          data-testid="edit-profile-doc-type"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Expiry (optional)</Label>
                        <Input
                          type="date"
                          value={editProfile.newDocExpiry || ''}
                          onChange={(e) => setEditProfile({ ...editProfile, newDocExpiry: e.target.value })}
                          data-testid="edit-profile-doc-expiry"
                        />
                      </div>
                    </div>
                    <label className="block">
                      <span className="text-xs text-gray-600 block mb-1">Upload file (PDF, JPG, PNG · max 10 MB)</span>
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-[#1e3a5f] file:text-white file:font-semibold hover:file:bg-[#254d7f] cursor-pointer"
                        data-testid="edit-profile-doc-file"
                        onChange={async (e) => {
                          const file = e.target.files && e.target.files[0];
                          if (!file) return;
                          const type = editProfile.newDocType || '';
                          const expiry = editProfile.newDocExpiry || '';
                          await handleUploadDoc(file, type, expiry);
                          setEditProfile((prev) => prev ? { ...prev, newDocType: '', newDocExpiry: '' } : prev);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {profileErr && (
                <p className="text-sm text-red-600" data-testid="edit-profile-error">{profileErr}</p>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setEditProfile(null)}
                  className="flex-1"
                  disabled={savingProfile}
                  data-testid="edit-profile-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="flex-1 bg-[#1e3a5f] hover:bg-[#254d7f] text-white"
                  data-testid="edit-profile-save"
                >
                  {savingProfile ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminPilotRegistrations;
